import type { Course } from '@/types/course';
import type { ExpertData } from '@/types/expert';
import type { Partner } from '@/types/partner';
import type { VideoCourseResponse, VideoResponse } from '@/types/video';
import { DEFAULT_STRAPI_SORT, getStrapiData } from '@/services/strapiService';
import type { StrapiResponse } from '@/types/strapi';
import { sharedConfig } from '@/config/sharedConfig';

export const getCourses = async (
  limit: number = 5,
  sort: string[] = DEFAULT_STRAPI_SORT,
  start: number = 0,
  categories?: string[],
  locale: string = sharedConfig.defaultLocale,
): Promise<StrapiResponse<Course[]>> => {
  const queryParams: { [key: string]: string | string[] } = {
    populate: 'cover, expert.picture',
    'pagination[limit]': limit.toString(),
    'pagination[start]': start.toString(),
    sort: sort,
    locale,
  };

  if (categories && categories.length > 0) {
    categories.forEach((slug, index) => {
      queryParams[`filters[$or][${index}][categories][slug][$eq]`] = slug;
    });
  }

  return getStrapiData<Course[]>('courses', queryParams);
};

export const getExperts = async (
  limit: number = 10,
  skip: number = 0,
  categories: string[] = [],
  locale: string = sharedConfig.defaultLocale,
): Promise<StrapiResponse<ExpertData[]>> => {
  const queryParams: { [key: string]: string | string[] } = {
    populate: 'languages,picture,categories',
    'pagination[limit]': limit.toString(),
    'pagination[start]': skip.toString(),
    locale,
    'filters[active][$eq]': 'true',
    sort: DEFAULT_STRAPI_SORT,
  };

  if (categories.length > 0) {
    categories.forEach((slug, index) => {
      queryParams[`filters[$or][${index}][categories][slug][$eq]`] = slug;
    });
  }

  return getStrapiData<ExpertData[]>('experts', queryParams);
};

export const getVideoCourses = async (
  limit: number = 10,
  categories: string[] = [],
  start: number = 0,
  locale: string = sharedConfig.defaultLocale,
): Promise<StrapiResponse<VideoCourseResponse[]>> => {
  const queryParams: { [key: string]: string | string[] } = {
    populate: 'videos, expert.picture, thumbnail, languages, categories',
    'pagination[limit]': limit.toString(),
    'pagination[start]': start.toString(),
    locale,
    sort: DEFAULT_STRAPI_SORT,
  };

  if (categories.length > 0) {
    categories.forEach((slug, index) => {
      queryParams[`filters[$or][${index}][categories][slug][$eq]`] = slug;
    });
  }

  return getStrapiData<VideoCourseResponse[]>('video-courses', queryParams);
};

export const getVideos = async (
  limit: number = 10,
  categories: string[] = [],
  start: number = 0,
  locale: string = sharedConfig.defaultLocale,
): Promise<StrapiResponse<VideoResponse[]>> => {
  const queryParams: { [key: string]: string | string[] } = {
    populate: 'thumbnail, expert.picture, languages, categories',
    'pagination[limit]': limit.toString(),
    'pagination[start]': start.toString(),
    locale,
    'filters[single][$eq]': 'true',
    sort: DEFAULT_STRAPI_SORT,
  };

  if (categories.length > 0) {
    categories.forEach((slug, index) => {
      queryParams[`filters[$or][${index}][categories][slug][$eq]`] = slug;
    });
  }

  return getStrapiData<VideoResponse[]>('videos', queryParams);
};

export const getPartners = async (
  limit: number = 10,
  start: number = 0,
  sort: string[] = DEFAULT_STRAPI_SORT,
  categories: string[] = [],
  locale: string = sharedConfig.defaultLocale,
): Promise<StrapiResponse<Partner[]>> => {
  const queryParams: { [key: string]: string | string[] } = {
    populate: 'cover,logo,languages,categories,features',
    'pagination[limit]': limit.toString(),
    'pagination[start]': start.toString(),
    sort: sort,
    locale,
  };

  if (categories.length > 0) {
    categories.forEach((slug, index) => {
      queryParams[`filters[$or][${index}][categories][slug][$eq]`] = slug;
    });
  }

  return getStrapiData<Partner[]>('partners', queryParams);
};
